import { View, animate } from '@play-co/timestep-core/ui';
import { spineLoader } from '@play-co/timestep-spine';
import { DOWNSCALE_FACTOR } from 'src/conf/scale';
import SpineAnimationView from 'src/game/components/shared/SpineAnimationView';
import { dreamDriftWearPet } from 'src/game/dreamDrift/dreamDrift.costumes';
import { getPet } from 'src/game/pet';
import { createUIPet } from 'src/game/pet/pet.utils';
import { petAnimationNames } from 'src/replicant/pet/PetAssets';
import sounds from 'src/lib/sounds';
import type { GameScene } from '../../minigame/match2-timestep/game/GameScene';
import { waitFor } from '../../minigame/match2-timestep/lib/util/waitFor';

/** Spine export used by the Rumble Hero opening presentation. */
export const rumbleHeroPowerAnimationAsset = {
  loadMethod: spineLoader,
  url: 'assets/ui/dreamDrift/powerRumble/spine.Dreamdrift.rumble',
};

/** Delay from Spine playback start to the first authored lightning blast key. */
const RUMBLE_IMPACT_DELAY_MS = 667;

/** Scale authored on the placeholder bone in the Spine setup pose. */
const RUMBLE_PET_SETUP_SCALE = 0.5;

/** Callback that applies the Rumble board effect at the animation impact. */
export type RumbleHeroPowerImpact = () => void;

/**
 * Shake the complete Dream Drift scene around its current position.
 *
 * @param sceneRoot Root view containing the puzzle and Rumble presentation.
 * @returns Nothing after the 400ms shake restores the original position.
 */
async function shakeRumbleScene(sceneRoot: View): Promise<void> {
  // Preserve the responsive scene position so the shake cannot shift its layout.
  const originX = sceneRoot.style.x ?? 0;
  const originY = sceneRoot.style.y ?? 0;

  // Alternate decreasing offsets before settling exactly at the origin.
  await animate(sceneRoot)
    .now({ x: originX - 18, y: originY + 8 }, 50, animate.linear)
    .then({ x: originX + 15, y: originY - 9 }, 60, animate.linear)
    .then({ x: originX - 12, y: originY + 6 }, 60, animate.linear)
    .then({ x: originX + 9, y: originY - 5 }, 60, animate.linear)
    .then({ x: originX - 6, y: originY + 3 }, 60, animate.linear)
    .then({ x: originX, y: originY }, 110, animate.easeOut)
    .toPromise();
}

/**
 * Play the supplied Rumble Hero Spine animation over the Dream Drift scene.
 *
 * @param game Active Dream Drift Match-2 runtime.
 * @param onImpact Callback applying the synchronized board effect.
 * @returns Nothing after the complete presentation finishes.
 */
export async function runRumbleHeroPowerAnimation(
  game: GameScene,
  onImpact: RumbleHeroPowerImpact,
): Promise<void> {
  // Load the supplied Spine export before changing the visible puzzle scene.
  await spineLoader(rumbleHeroPowerAnimationAsset.url);

  // Resolve the standard Match-2 scene hierarchy from the game stage.
  const puzzleContainer = game.stage.view.getSuperview();
  const sceneRoot = puzzleContainer?.getSuperview();
  if (!puzzleContainer || !sceneRoot) return;

  // Keep the black dim behind the puzzle grid while covering the scene beneath it.
  const dim = new View({
    superview: sceneRoot,
    width: sceneRoot.style.width,
    height: sceneRoot.style.height,
    backgroundColor: '#000000',
    opacity: 0,
    canHandleEvents: false,
    zIndex: (puzzleContainer.style.zIndex ?? 0) - 0.5,
  });

  // Center the native 1080x1920 Spine composition over the responsive scene.
  const animationContainer = new View({
    superview: sceneRoot,
    x: sceneRoot.style.width / 2,
    y: sceneRoot.style.height / 2,
    scale: DOWNSCALE_FACTOR,
    canHandleEvents: false,
    zIndex: 100000,
  });

  // Instantiate the supplied effects animation behind its character placeholder.
  const spine = new SpineAnimationView({
    superview: animationContainer,
    spinePath: rumbleHeroPowerAnimationAsset.url,
    x: 0,
    y: 0,
    zIndex: 0,
    visible: false,
  });

  // Instantiate the player's pet and dress it as Rumble Hero.
  const species = getPet().species ?? 'dog';
  const pet = createUIPet('normal', species);
  pet.setAnimation(petAnimationNames[species].angry);
  dreamDriftWearPet(pet, 'rumbleHero');
  pet.updateOpts({
    superview: animationContainer,
    anchorY: 300,
    offsetY: -300,
    scale: 2.4,
    zIndex: 1,
  });

  /** Keep the pet below the authored placeholder for the complete animation. */
  const updatePetWithSpine = (): void => {
    const bone = spine.skeleton.findBone('placeholder_bear');
    if (!bone) return;

    pet.updateOpts({
      x: bone.worldX,
      y: bone.worldY + 50,
      r: (bone.getWorldRotationX() * Math.PI) / 180,
      scaleX: bone.getWorldScaleX() * RUMBLE_PET_SETUP_SCALE,
      scaleY: bone.getWorldScaleY() * RUMBLE_PET_SETUP_SCALE,
    });
  };
  animationContainer.setTick(updatePetWithSpine);

  // Dim the scene before revealing the hero power composition.
  await animate(dim).then({ opacity: 0.5 }, 300, animate.linear).toPromise();

  // Start the one-shot animation after every presentation element is ready.
  spine.show();
  const { complete } = spine.start({ id: 'rumble', mix: 0 });

  // Trigger the board effect and screen shake on the authored impact frame.
  const impact = waitFor(RUMBLE_IMPACT_DELAY_MS).then(() => {
    // Play one lightning strike with the authored blast and board impact.
    sounds.sfx.play('dreamDriftLightning');
    onImpact();
    void shakeRumbleScene(sceneRoot);
  });
  await Promise.all([complete, impact]);

  // Restore the scene behind the still-bright puzzle grid.
  await animate(dim).then({ opacity: 0 }, 300, animate.linear).toPromise();

  // Remove all temporary animation state after the presentation finishes.
  animationContainer.setTick(null);
  spine.state.clearTracks();
  spine.state.clearListeners();
  animationContainer.removeFromSuperview();
  dim.removeFromSuperview();
}
